import React, { FC } from 'react';
import { ViewType } from 'api/views/types';
import { SavedViews as ViewsComponent } from 'components/molecules/views';
import { useViewActions } from 'components/molecules/views/view-actions';
import { GroupFieldsMeta } from 'components/pages/filter-transformer';
import { useUpdateTaskViewMutation } from 'queries/task-view';
import { UsersGroupedByCompanyComponents } from '../filter-editors';
import { useAllTasksKanbanGroupBy } from '../filter-menu-sidebar';
import { useGroupedFilterMenu } from '../filter-menu-sidebar/use-grouped-filter-menu';
import { ColumnOrder } from '../grid-custom-column';
import { UserEditorComponents } from './criteria/editor-components';
import { FilterableField, GroupedOptions } from './fields';
import { GetSelectEditorComponents } from './filter-box/utils';
import {
  GroupedFilter,
  TaskGroupedFilterPopover,
} from './grouped-filter-popover';
import {
  groupViewDefinitionComparator,
  isValidViewDefinitionForGroupedView,
} from './views';

export interface Props {
  isFieldsLoading: boolean;
  filter: GroupedFilter;
  fields: GroupedOptions<FilterableField>[];
  selectFilter: (filter: GroupedFilter) => void;
  clearFilters: () => void;
  lookup: GroupFieldsMeta;
  taskLookup: Record<string, FilterableField>;
  columnOrder?: ColumnOrder[];
  defaultColumnOrder?: ColumnOrder[];
  handleColumnOrderChange?: (columnOrder: ColumnOrder[]) => void;
}

const getSelectEditorComponents: GetSelectEditorComponents = (field) => {
  if (field.fieldName === 'customer') {
    return UserEditorComponents;
  }

  if (field.fieldName === 'teamMembers') {
    return UsersGroupedByCompanyComponents;
  }

  return undefined;
};

export const GroupedFilterView: FC<Props> = ({
  filter,
  fields,
  isFieldsLoading,
  selectFilter,
  clearFilters,
  lookup,
  taskLookup,
  columnOrder = [],
  defaultColumnOrder = [],
  handleColumnOrderChange = () => {},
}) => {
  const [groupBy, setGroupBy] = useAllTasksKanbanGroupBy(
    taskLookup,
    isFieldsLoading,
  );

  const { onUpdate: onTaskViewUpdate } = useUpdateTaskViewMutation();
  const {
    taskListViews,
    viewDefinitionTransformer,
    currentViewId,
    onViewSelect,
    defaultViewsList,
    defaultViewId,
    defaultSharedFilter,
  } = useGroupedFilterMenu({
    fields: fields.flatMap((field) => field.options),
    lookup,
    isFieldsLoading,
    selectFilter,
    clearFilters,
    setGroupBy,
    taskLookup,
    filter,
    handleColumnOrderChange,
    defaultColumnOrder,
  });

  const { onViewCreate, onViewUpdate, onViewDelete } = useViewActions({
    views: taskListViews,
    setViews: onTaskViewUpdate,
    viewType: ViewType.AllTasks,
    transformer: viewDefinitionTransformer,
  });

  return (
    <ViewsComponent
      filterComponent={
        <TaskGroupedFilterPopover
          filter={filter}
          fields={fields}
          loading={isFieldsLoading}
          onChange={selectFilter}
          reset={clearFilters}
          taskLookup={taskLookup}
          hideClearFilter
          getSelectEditorComponents={getSelectEditorComponents}
        />
      }
      views={taskListViews}
      defaultViewsList={defaultViewsList}
      defaultViewId={defaultViewId}
      currentViewDefinition={{
        filter,
        groupBy,
        columnOrder,
      }}
      onViewSelect={onViewSelect}
      isValidViewDefinition={isValidViewDefinitionForGroupedView(
        defaultColumnOrder,
      )}
      viewDefinitionComparator={groupViewDefinitionComparator(
        defaultColumnOrder,
      )}
      onViewCreate={onViewCreate}
      onViewDelete={onViewDelete}
      onViewUpdate={onViewUpdate}
      selectedViewId={currentViewId}
      includeSavedViews={false}
      defaultSharedFilter={defaultSharedFilter}
    />
  );
};
